// ═══════════════════════════════════════════════════════════
// NEON CYBER RACER THEME CONFIGURATION
// ═══════════════════════════════════════════════════════════

/**
 * Centralized theme configuration for the Neon Cyber Racer aesthetic
 * Inspired by cyberpunk street racing and arcade precision
 */

export const Theme = {
    // ═══════════════════════════════════════════════════════════
    // COLOR PALETTE
    // ═══════════════════════════════════════════════════════════
    colors: {
        // Primary Neon Colors
        neonCyan: '#00FFFF',        // Ball core, UI highlights, small points
        hotMagenta: '#FF00FF',      // Aim arrow, medium points, trails
        electricYellow: '#FFFF00',  // Corners/checkpoints, large points, warnings
        cyberPurple: '#8A2BE2',     // Obstacles, speed boosts
        deepGridBlack: '#0A0A0A',   // Background base
        glowWhite: '#FFFFFF',       // Outlines, particles

        // Secondary Colors
        neonGreen: '#00FF00',       // Success indicators, combo chains
        neonOrange: '#FF6600',      // Warning states
        neonPink: '#FF1493',        // Special effects
        darkPurple: '#4B0082',      // Obstacle accents

        // Gradients & Effects
        gridBlue: '#001133',        // Background grid lines
        electricBlue: '#0099FF',    // Energy effects
        plasmaPink: '#FF69B4',      // Particle trails

        // UI Colors
        uiCyan: 'rgba(0, 255, 255, 0.9)',
        uiMagenta: 'rgba(255, 0, 255, 0.9)',
        uiYellow: 'rgba(255, 255, 0, 0.9)',
        uiWhite: 'rgba(255, 255, 255, 0.8)',
        uiDanger: 'rgba(255, 0, 0, 0.9)',
    },

    // ═══════════════════════════════════════════════════════════
    // GLOW EFFECTS
    // ═══════════════════════════════════════════════════════════
    glow: {
        // Glow intensity levels
        subtle: 10,
        medium: 20,
        intense: 30,
        extreme: 40,

        // Blur amounts for different elements
        ballGlow: 25,
        cornerGlow: 30,
        particleGlow: 15,
        uiGlow: 20,
        pickupGlow: 20,
        obstacleGlow: 25,

        // Color-specific glows
        cyanGlow: 'rgba(0, 255, 255, 0.8)',
        magentaGlow: 'rgba(255, 0, 255, 0.8)',
        yellowGlow: 'rgba(255, 255, 0, 0.8)',
        purpleGlow: 'rgba(138, 43, 226, 0.8)',
    },

    // ═══════════════════════════════════════════════════════════
    // BALL (RACER ORB) THEME
    // ═══════════════════════════════════════════════════════════
    ball: {
        coreColor: '#00FFFF',           // Neon cyan core
        glowColor: '#00FFFF',           // Matching glow
        trailColor: '#FF00FF',          // Hot magenta trail
        boostColor: '#FFFF00',          // Yellow when boosted

        // Energy core animation
        energyCoreSize: 0.6,            // 60% of ball size
        energyPulseSpeed: 2.0,          // Pulsations per second

        // Flame trail when boosted
        flameColors: ['#FFFF00', '#FF6600', '#FF00FF'],
        trailParticleCount: 15,         // More particles = better trail
        trailLength: 40,                // Extended trail length

        // Squash & stretch enhancement
        squashStretchAmount: 1.3,       // More pronounced (was 1.2)

        // Glow rings
        glowRings: 3,                   // Multiple glow layers
        maxGlowRadius: 60,              // Maximum glow extent
    },

    // ═══════════════════════════════════════════════════════════
    // CORNERS (RACE GATES/PORTALS)
    // ═══════════════════════════════════════════════════════════
    corners: {
        primaryColor: '#FFFF00',        // Electric yellow
        secondaryColor: '#00FFFF',      // Cyan accent
        glowColor: 'rgba(255, 255, 0, 0.7)',

        // Portal/gate visual
        gateWidth: 80,                  // Width of gate
        gateHeight: 80,                 // Height of gate
        pulseSpeed: 1.5,                // Pulsations per second

        // Text overlay
        checkpointText: 'CHECK',
        textColor: '#FFFFFF',
        textGlow: '#FFFF00',

        // Particle burst on hit
        burstParticleCount: 50,         // Increased from 30
        burstColors: ['#FFFF00', '#00FFFF', '#FFFFFF'],

        // Shrinking animation
        initialSize: 70,
        finalSize: 25,
        shrinkSpeed: 0.8,
    },

    // ═══════════════════════════════════════════════════════════
    // POINT PICKUPS (ENERGY SHARDS)
    // ═══════════════════════════════════════════════════════════
    pickups: {
        // Tiered color system
        tier1Color: '#00FFFF',          // Cyan shard (+10)
        tier1Glow: 'rgba(0, 255, 255, 0.6)',
        tier1Size: 18,

        tier2Color: '#FF00FF',          // Magenta crystal (+25)
        tier2Glow: 'rgba(255, 0, 255, 0.6)',
        tier2Size: 22,

        tier3Color: '#FFFF00',          // Gold plasma orb (+50)
        tier3Glow: 'rgba(255, 215, 0, 0.8)',
        tier3Size: 28,

        // Animation
        spinSpeed: 3.0,                 // Rotations per second
        pulseSpeed: 2.0,                // Pulse cycles per second
        pulseAmount: 0.2,               // Scale variation (±20%)

        // Sparkle particles
        sparkleCount: 8,
        sparkleColors: ['#FFFFFF', '#00FFFF', '#FF00FF', '#FFFF00'],

        // Float toward ball effect
        magnetDistance: 100,            // Start pulling at this distance
        magnetSpeed: 200,               // Pull speed
    },

    // ═══════════════════════════════════════════════════════════
    // OBSTACLES (CYBER DRONES)
    // ═══════════════════════════════════════════════════════════
    obstacles: {
        bodyColor: '#8A2BE2',           // Cyber purple
        eyeColor: '#FF0000',            // Red warning eye
        exhaustColor: '#FF00FF',        // Magenta exhaust
        glowColor: 'rgba(138, 43, 226, 0.6)',

        // Drone design
        coreSize: 30,
        eyeSize: 8,
        exhaustLength: 15,

        // Animation
        spinSpeed: 2.0,                 // Rotations per second
        pulseSpeed: 2.5,                // Pulse cycles per second

        // Trail effect
        trailLength: 10,
        trailColor: '#8A2BE2',
        trailParticleSize: 4,

        // Explosion on collision
        explodeColors: ['#8A2BE2', '#FF00FF', '#FF0000'],
        explodeParticleCount: 40,
    },

    // ═══════════════════════════════════════════════════════════
    // POWER-UPS (TURBO NITRO)
    // ═══════════════════════════════════════════════════════════
    powerUps: {
        boostColor: '#00FFFF',          // Blue flame canister
        boostGlow: 'rgba(0, 191, 255, 0.8)',

        // Nitro visual
        canisterColor: '#0099FF',
        flameColor: '#00FFFF',

        // Pickup effect
        auraColor: '#00BFFF',
        auraRadius: 40,

        // Active boost effects on ball
        activeTintColor: '#00FFFF',
        activeParticleCount: 20,
        activeTrailMultiplier: 1.8,
    },

    // ═══════════════════════════════════════════════════════════
    // PARTICLES & EFFECTS
    // ═══════════════════════════════════════════════════════════
    particles: {
        // Enhanced particle counts
        cornerHit: 50,                  // Increased from 30
        wallBounce: 12,                 // Increased from 8
        pickupCollect: 20,              // New
        obstacleExplode: 40,            // New

        // Particle colors (vibrant neon set)
        colors: [
            '#00FFFF',  // Cyan
            '#FF00FF',  // Magenta
            '#FFFF00',  // Yellow
            '#00FF00',  // Green
            '#FF6600',  // Orange
            '#FFFFFF',  // White
            '#FF1493',  // Pink
        ],

        // Particle physics
        gravityMultiplier: 1.2,         // Slightly stronger gravity
        frictionMultiplier: 0.96,       // Less friction = longer trails

        // Size range
        minSize: 3,                     // Increased from 2
        maxSize: 8,                     // Increased from 6

        // Lifetime
        minLife: 0.3,
        maxLife: 1.5,
    },

    // ═══════════════════════════════════════════════════════════
    // BACKGROUND (PARALLAX CYBER GRID)
    // ═══════════════════════════════════════════════════════════
    background: {
        baseColor: '#0A0A0A',           // Deep black

        // Grid layers (parallax)
        farLayer: {
            color: 'rgba(0, 50, 100, 0.2)',     // Deep blue stars/grid
            lineWidth: 1,
            gridSize: 100,
            scrollSpeed: 0.2,           // Slowest layer
        },

        midLayer: {
            color: 'rgba(0, 100, 150, 0.3)',    // Buildings/structures
            lineWidth: 1.5,
            gridSize: 60,
            scrollSpeed: 0.5,           // Medium layer
        },

        nearLayer: {
            color: 'rgba(0, 255, 255, 0.15)',   // Neon signs/foreground
            lineWidth: 2,
            gridSize: 40,
            scrollSpeed: 1.0,           // Speed matches ball velocity
        },

        // Scan lines
        scanLineColor: 'rgba(0, 255, 255, 0.05)',
        scanLineSpacing: 4,

        // Vignette
        vignetteColor: 'rgba(0, 0, 0, 0.4)',
        vignetteRadius: 0.7,            // 70% from center
    },

    // ═══════════════════════════════════════════════════════════
    // UI ELEMENTS
    // ═══════════════════════════════════════════════════════════
    ui: {
        // Fonts
        primaryFont: 'bold 32px "Courier New", monospace',
        secondaryFont: '24px "Courier New", monospace',
        titleFont: 'bold 70px "Courier New", monospace',

        // HUD colors
        scoreColor: '#00FFFF',          // Cyan
        timeColor: '#FFFF00',           // Yellow
        timeLowColor: '#FF0000',        // Red when low

        // Progress bars
        barHeight: 12,
        barWidth: 220,
        barBorderColor: '#FFFFFF',
        barBorderWidth: 2,

        // Score bar
        scoreBarColors: ['#00FFFF', '#00BFFF'],     // Cyan gradient

        // Time bar
        timeBarColors: ['#00FF00', '#FFFF00'],      // Green to yellow
        timeBarLowColors: ['#FF0000', '#FF6600'],   // Red to orange

        // Buttons
        buttonColor: '#FFFF00',
        buttonHoverColor: '#00FFFF',
        buttonGlow: 'rgba(255, 255, 0, 0.6)',

        // Combo chain display
        comboColor: '#FF00FF',          // Magenta
        comboGlow: 'rgba(255, 0, 255, 0.8)',
        comboTextSize: 48,

        // Popups
        popupColor: '#FFFF00',
        popupStroke: '#000000',
        popupStrokeWidth: 4,
    },

    // ═══════════════════════════════════════════════════════════
    // SCREEN EFFECTS
    // ═══════════════════════════════════════════════════════════
    effects: {
        // Screen shake intensities
        cornerHitShake: 10,
        wallBounceShake: 3,
        obstacleHitShake: 25,
        boostActivateShake: 6,

        // Flash effects
        flashDuration: 0.15,            // Seconds
        flashColors: {
            cornerHit: 'rgba(255, 255, 0, 0.3)',
            boost: 'rgba(0, 255, 255, 0.2)',
            damage: 'rgba(255, 0, 0, 0.4)',
        },

        // Motion blur (simulated via trails)
        motionBlurLength: 5,            // Number of ghost images
        motionBlurAlpha: 0.3,           // Starting alpha

        // Chromatic aberration
        chromaticOffset: 2,             // Pixel offset for RGB split

        // Bloom effect
        bloomThreshold: 200,            // Brightness threshold
        bloomIntensity: 0.4,
    },

    // ═══════════════════════════════════════════════════════════
    // GAMEPLAY ENHANCEMENTS
    // ═══════════════════════════════════════════════════════════
    gameplay: {
        // Combo system
        comboWindow: 2.0,               // Seconds to maintain combo
        comboMultipliers: [1, 2, 3, 5], // x1, x2, x3, x5
        comboMinHits: 2,                // Minimum hits to start combo

        // Aiming laser
        laserColor: '#FF00FF',          // Magenta laser
        laserWidth: 3,
        laserLength: 200,
        laserSegments: 10,              // Dashed line segments
        laserAlpha: 0.6,

        // Predicted path
        pathColor: 'rgba(255, 0, 255, 0.3)',
        pathDotSize: 4,
        pathDotCount: 15,

        // Slow-mo effect while aiming
        slowMoFactor: 0.3,              // 30% speed
        slowMoTint: 'rgba(138, 43, 226, 0.1)',
    },

    // ═══════════════════════════════════════════════════════════
    // PERFORMANCE SETTINGS
    // ═══════════════════════════════════════════════════════════
    performance: {
        // Mobile optimization
        mobileParticleReduction: 0.5,   // 50% particles on mobile
        mobileGlowReduction: 0.7,       // 70% glow intensity on mobile

        // LOD (Level of Detail)
        enableLOD: true,
        lowFpsThreshold: 30,            // Switch to low detail below 30 FPS

        // Particle pooling
        maxParticles: 500,              // Maximum concurrent particles
        particlePoolSize: 1000,         // Pre-allocated pool
    },

    // ═══════════════════════════════════════════════════════════
    // ANIMATION TIMINGS
    // ═══════════════════════════════════════════════════════════
    animation: {
        // Menu pulsing
        menuPulseSpeed: 0.8,            // Cycles per second

        // Transition durations
        fadeInDuration: 0.5,
        fadeOutDuration: 0.3,

        // Easing preferences
        defaultEasing: 'easeOutCubic',
        bounceEasing: 'easeOutElastic',
        snapEasing: 'easeOutBack',
    },
};

// ═══════════════════════════════════════════════════════════
// UTILITY FUNCTIONS FOR THEME
// ═══════════════════════════════════════════════════════════

/**
 * Create a radial gradient glow effect
 */
export function createNeonGlow(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    color: string,
    intensity: number = 1
): CanvasGradient {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

    // Extract RGB from hex color
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.9 * intensity})`);
    gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, ${0.6 * intensity})`);
    gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${0.3 * intensity})`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

    return gradient;
}

/**
 * Apply neon glow effect to canvas context
 */
export function applyNeonGlow(
    ctx: CanvasRenderingContext2D,
    color: string,
    blur: number = 20
): void {
    ctx.shadowColor = color;
    ctx.shadowBlur = blur;
}

/**
 * Clear neon glow effect
 */
export function clearNeonGlow(ctx: CanvasRenderingContext2D): void {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
}

/**
 * Get tier color for point pickup based on value
 */
export function getPickupTier(value: number): {
    color: string;
    glow: string;
    size: number;
} {
    if (value >= 50) {
        return {
            color: Theme.pickups.tier3Color,
            glow: Theme.pickups.tier3Glow,
            size: Theme.pickups.tier3Size,
        };
    } else if (value >= 25) {
        return {
            color: Theme.pickups.tier2Color,
            glow: Theme.pickups.tier2Glow,
            size: Theme.pickups.tier2Size,
        };
    } else {
        return {
            color: Theme.pickups.tier1Color,
            glow: Theme.pickups.tier1Glow,
            size: Theme.pickups.tier1Size,
        };
    }
}

/**
 * Create cyber grid pattern
 */
export function drawCyberGrid(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    gridSize: number,
    color: string,
    lineWidth: number,
    offset: number = 0
): void {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;

    // Vertical lines
    for (let x = offset % gridSize; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }

    // Horizontal lines
    for (let y = offset % gridSize; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }

    ctx.restore();
}

/**
 * Flash screen effect
 */
export function drawScreenFlash(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    color: string,
    alpha: number
): void {
    if (alpha <= 0) return;

    ctx.save();
    ctx.fillStyle = color.replace(/[\d.]+\)$/, `${alpha})`);
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
}
